/* eslint-disable no-extra-boolean-cast */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
// @ts-ignore @TODO: add types for package
import { Confirm } from 'react-confirm-bootstrap';
import { cloneDeep, orderBy, set } from 'lodash';
import { v4 as uuid } from 'uuid';
import produce from 'immer';
import {
  PriceRuleOffsetType,
  PriceRuleSource,
} from '@upstart/harmony/lib/constants';
import { TieredAprConfig } from 'components/PricingEditor/Tiers/types';
import DealerPricingService from 'services/DealerPricingService';
import { CreditPricing } from '@upstart/harmony/lib/legos/creditPricingType';
import { transformToAprConfig } from 'components/PricingEditor/Tiers/creditTiersAdapter';
import {
  PriceRulesEntry,
  PriceRulesRevision,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { getConfigurationId } from '@upstart/harmony/lib/configurations-utils';
import { Action } from 'util/amplitudeEvents';
import DealerService from '../../services/DealerService';
import { DefaultRulesTableForUsed } from './DefaultRulesTableForUsed';
import { ModelRulesTableForUsed } from './ModelRulesTableForUsed';
import {
  DeletedAPRConfigs,
  PriceRulesEntryUpdater,
  PricingMatrixEditorForUsedProps,
} from './types';
import ErrorMessage from '../ErrorMessage';
import { getClientTypeName } from '../../util/constants';
import './PricingMatrixEditor.scss';
import {
  getDefaultPriceRulesEntry,
  getOppositeClientType,
  sortCarRulesBasedOnMaxYear,
} from './utils';

export const PricingMatrixEditorForUsed: FunctionComponent<
  PricingMatrixEditorForUsedProps
> = ({
  dealerPriceRules,
  dealer,
  users,
  clientType,
  condition,
  onChange,
  onSave,
  fetching,
  sendAmplitudeEvent,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | object>();
  const [resetTrigger, setResetTrigger] = useState(0);
  const [priceRules, setPriceRules] = useState<PriceRulesEntry>(
    getDefaultPriceRulesEntry(),
  );

  const [hasClickedOnCopyButton, setHasClickedOnCopyButton] =
    useState<boolean>(false);
  const [configurationIdsForDeletion, setConfigurationIdsForDeletion] =
    useState<string[]>([]);

  const [carRuleStatuses, setCarRuleStatuses] = useState<
    {
      isNewRecord: boolean;
    }[]
  >([]);

  const [tieredAprConfigs, setTieredAprConfigs] = useState<
    {
      tieredAprConfig: TieredAprConfig | null;
    }[]
  >([]);

  const [defaultTieredAprConfig, setDefaultTieredAprConfig] = useState<{
    tieredAprConfig: TieredAprConfig | null;
  }>({
    tieredAprConfig: null,
  });

  const [
    errorMessagesForYearRangeEntries,
    setErrorMessagesForYearRangeEntries,
  ] = useState<
    {
      maxYear: string | null;
      minYear: string | null;
    }[]
  >([]);

  const [reactRowKeys, setReactRowKeys] = useState<string[]>([]);

  const setTieredAprConfig = (
    index: number,
    tieredAprConfig: TieredAprConfig,
  ) => {
    const clonedAprConfigs = [...tieredAprConfigs];
    clonedAprConfigs[index].tieredAprConfig = tieredAprConfig;
    setTieredAprConfigs(clonedAprConfigs);
  };

  const deleteTieredAprConfig = (index: number) => {
    const clonedAprConfigs = [...tieredAprConfigs];
    clonedAprConfigs.splice(index, 1);
    setTieredAprConfigs(clonedAprConfigs);
  };

  const deleteErrorMessagesForYearRangeEntry = (index: number) => {
    const clonedErrorMessagesForYearRangeEntries = [
      ...errorMessagesForYearRangeEntries,
    ];
    clonedErrorMessagesForYearRangeEntries.splice(index, 1);
    setErrorMessagesForYearRangeEntries(clonedErrorMessagesForYearRangeEntries);
  };

  const deleteReactRowKey = (index: number) => {
    const clonedReactRowKeys = [...reactRowKeys];
    clonedReactRowKeys.splice(index, 1);
    setReactRowKeys(clonedReactRowKeys);
  };

  const deleteCarRuleStatusEntry = (index: number) => {
    const clonedCarRuleStatuses = [...carRuleStatuses];
    clonedCarRuleStatuses.splice(index, 1);
    setCarRuleStatuses(clonedCarRuleStatuses);
  };

  const setErrorMessageForYearInput = (
    index: number,
    key: 'maxYear' | 'minYear',
    errorMessageForCarModel: string | null,
  ) => {
    const clonedErrorMessages = [...errorMessagesForYearRangeEntries];
    clonedErrorMessages[index][key] = errorMessageForCarModel;
    setErrorMessagesForYearRangeEntries(clonedErrorMessages);
  };

  const hasInvalidYearInputs = () =>
    !!errorMessagesForYearRangeEntries.find((e) => !!e.maxYear || !!e.minYear);

  const addNewModelYearRangeEntry = () => {
    setPriceRules(
      produce((draft) => {
        if (draft.carRules) {
          draft.carRules.push({
            id: 'year_range',
            maxYear: undefined,
            minYear: undefined,
            priceSource:
              priceRules?.defaultRule?.priceSource ||
              PriceRuleSource.FEED_PRICE,
            offsetType:
              priceRules?.defaultRule?.offsetType || PriceRuleOffsetType.ADD,
            offsetValue: priceRules?.defaultRule?.offsetValue ?? undefined,
          });
        }
      }),
    );

    setTieredAprConfigs([
      ...tieredAprConfigs,
      {
        tieredAprConfig: null,
      },
    ]);

    setErrorMessagesForYearRangeEntries([
      ...errorMessagesForYearRangeEntries,
      {
        maxYear: 'Max Year is a required field.',
        minYear: 'Min Year is a required field.',
      },
    ]);
    setReactRowKeys([...reactRowKeys, uuid()]);
    setCarRuleStatuses([
      ...carRuleStatuses,
      {
        isNewRecord: true,
      },
    ]);
  };

  const deleteModelYearRangeEntry = (index: number) => {
    updatePriceRules((newPriceRules) => {
      if (newPriceRules.carRules) {
        if (
          !hasClickedOnCopyButton &&
          !carRuleStatuses[index].isNewRecord &&
          newPriceRules.carRules[index].aprConfig?.enabled &&
          newPriceRules?.carRules?.[index]?.id &&
          dealer
        ) {
          const configurationId = getConfigurationId({
            dealerId: dealer?.id,
            clientType,
            condition,
            modelYearRangeId: newPriceRules?.carRules?.[index]?.id,
          });
          setConfigurationIdsForDeletion([
            ...configurationIdsForDeletion,
            configurationId,
          ]);
        }
        newPriceRules.carRules.splice(index, 1);
      }

      deleteTieredAprConfig(index);
      deleteErrorMessagesForYearRangeEntry(index);
      deleteReactRowKey(index);
      deleteCarRuleStatusEntry(index);
    });
  };

  /**
   * Helper function to update the deleted APR Configs array.
   * Allows for an immer style object update https://immerjs.github.io/immer/docs/introduction#quick-example
   *
   * @param apply a function that edits a copy of priceRules
   */
  const updateDeletedAPRConfigs: DeletedAPRConfigs = (modelYearRangeId) => {
    if (dealer) {
      // use immer to edit priceRules with the given editor function and save the edited price rules
      setConfigurationIdsForDeletion(
        produce((currentDeletedAPRConfigs) => {
          currentDeletedAPRConfigs.push(
            getConfigurationId({
              dealerId: dealer.id,
              condition,
              clientType,
              modelYearRangeId,
            }),
          );
        }),
      );
      onChange(false);
    }
  };

  /**
   * Helper function to update the price rules object.
   * Allows for an immer style object update https://immerjs.github.io/immer/docs/introduction#quick-example
   *
   * @param apply a function that edits a copy of priceRules
   */
  const updatePriceRules: PriceRulesEntryUpdater = (apply) => {
    // use immer to edit priceRules with the given editor function and save the edited price rules
    setPriceRules(
      produce((newPriceRules) => {
        apply(newPriceRules);
      }),
    );
    onChange(false);
  };

  const onDealerSaved = () => {
    if (!dealer) return;
    let newDealerPriceRules = cloneDeep(dealerPriceRules);
    newDealerPriceRules = newDealerPriceRules || { id: dealer.id };

    const newPriceRules = { ...priceRules };

    const carRules = [...(newPriceRules?.carRules || [])];

    carRules.sort(sortCarRulesBasedOnMaxYear);

    newPriceRules.carRules = carRules;

    // save the updated pricing rules
    set(newDealerPriceRules, `data.${clientType}.${condition}`, {
      ...newPriceRules,
    });
    sendAmplitudeEvent(clientType, condition, Action.SAVE);

    onSave({
      ...newDealerPriceRules,
      metadata: {
        dealerId: dealer.id,
        configurationIdsForDeletion,
        condition,
        clientType,
      },
    });
  };

  const loadPriceRules = (newPriceRules: PriceRulesEntry) => {
    if (newPriceRules.carRules?.length) {
      newPriceRules.carRules.sort(sortCarRulesBasedOnMaxYear);
    }

    if (newPriceRules?.defaultRule && !!newPriceRules.defaultRule.aprConfig) {
      newPriceRules.defaultRule.aprConfig = {
        enabled: true,
      };
    }

    newPriceRules.carRules =
      newPriceRules?.carRules?.map((carRule) => {
        if (!!carRule.aprConfig) {
          carRule.aprConfig = {
            enabled: true,
          };
        }
        return carRule;
      }) || [];

    const tieredAprConfigsArray = [];
    const errorMessagesArray = [];
    const reactRowKeysArray = [];
    const carRuleStatusesArray = [];
    for (let index = 0; index < newPriceRules.carRules.length; index += 1) {
      tieredAprConfigsArray.push({
        tieredAprConfig: null,
      });
      errorMessagesArray.push({
        maxYear: null,
        minYear: null,
      });
      reactRowKeysArray.push(uuid());
      carRuleStatusesArray.push({
        isNewRecord: false,
      });
    }
    setErrorMessagesForYearRangeEntries(errorMessagesArray);
    setTieredAprConfigs(tieredAprConfigsArray);
    setCarRuleStatuses(carRuleStatusesArray);
    setReactRowKeys(reactRowKeysArray);
    setPriceRules(newPriceRules);
  };

  /**
   * This function will get Configuration Ids from the current ClientType PriceRules
   * @returns
   */

  const getConfigurationIdsForDeletionFromCurrentClientTypePriceRules = () => {
    let configurationsIdsForDeletion: string[] = [];

    if (dealer) {
      if (
        dealerPriceRules?.data?.[clientType]?.[condition]?.defaultRule
          ?.aprConfig?.enabled
      ) {
        configurationsIdsForDeletion.push(
          getConfigurationId({
            dealerId: dealer.id,
            clientType,
            condition,
          }),
        );
      }

      const carRulesConfigurationIds = (
        dealerPriceRules?.data?.[clientType]?.[condition]?.carRules || []
      )
        .filter((carRule) => carRule?.aprConfig?.enabled)
        .map((carRule) =>
          getConfigurationId({
            dealerId: dealer.id,
            clientType,
            condition,
            modelYearRangeId: carRule.id,
          }),
        );
      configurationsIdsForDeletion = [
        ...configurationsIdsForDeletion,
        ...carRulesConfigurationIds,
      ];
    }

    return configurationsIdsForDeletion;
  };

  /**
   * This function will get configuration Ids from the opposite ClientType PriceRules
   * @param priceRulesEntry
   * @returns
   */

  const getConfigurationsIdsFromOppClientTypePriceRules = (
    priceRulesEntry: PriceRulesEntry,
  ) => {
    let configurationsIdsForOppClientType: string[] = [];

    if (dealer) {
      const defaultConfigurationId = getConfigurationId({
        dealerId: dealer.id,
        condition,
        clientType: getOppositeClientType(clientType),
      });

      if (priceRulesEntry.defaultRule.aprConfig?.enabled) {
        configurationsIdsForOppClientType.push(defaultConfigurationId);
      }

      const carRulesConfigurationIds = (priceRulesEntry?.carRules || [])
        .filter((carRule) => carRule.aprConfig?.enabled)
        .map((carRule) =>
          getConfigurationId({
            dealerId: dealer.id,
            condition,
            clientType: getOppositeClientType(clientType),
            modelYearRangeId: carRule.id,
          }),
        );

      configurationsIdsForOppClientType = [
        ...configurationsIdsForOppClientType,
        ...carRulesConfigurationIds,
      ];
    }

    return configurationsIdsForOppClientType;
  };

  /**
   * This function will get configurations Info from the opposite Client Type Price Rules.
   * @param priceRulesEntry
   * @returns an object with priceRulesWithConfigurations, newDefaultTieredAprConfig and newTieredAprConfigs
   */
  const getConfigurationsInfoFromOppClientType = async (
    priceRulesEntry: PriceRulesEntry,
  ) => {
    const oppositeClientTypePriceRules = { ...priceRulesEntry };
    if (oppositeClientTypePriceRules?.carRules?.length) {
      oppositeClientTypePriceRules.carRules.sort(sortCarRulesBasedOnMaxYear);
    }

    const newDefaultTieredAprConfig: {
      tieredAprConfig: TieredAprConfig | null;
    } = {
      tieredAprConfig: null,
    };

    const newTieredAprConfigs: {
      tieredAprConfig: TieredAprConfig | null;
    }[] = [];

    const carRules = oppositeClientTypePriceRules?.carRules || [];

    for (let index = 0; index < carRules.length; index += 1) {
      newTieredAprConfigs.push({
        tieredAprConfig: null,
      });
    }

    if (dealer) {
      const configurationsIdsFromOppClientType =
        getConfigurationsIdsFromOppClientTypePriceRules(
          oppositeClientTypePriceRules,
        );

      if (configurationsIdsFromOppClientType.length) {
        const configurations = await DealerPricingService.getByIds(dealer.id, {
          configurationIds: configurationsIdsFromOppClientType,
        });

        if (oppositeClientTypePriceRules.defaultRule.aprConfig?.enabled) {
          const defaultConfigurationId = getConfigurationId({
            dealerId: dealer.id,
            condition,
            clientType: getOppositeClientType(clientType),
          });

          const defaultPriceRulesForOppClient = configurations.find(
            (configuration: { id: string; data: CreditPricing }) =>
              configuration.id === defaultConfigurationId,
          );

          if (defaultPriceRulesForOppClient) {
            oppositeClientTypePriceRules.defaultRule.creditPricing =
              defaultPriceRulesForOppClient.data;
            newDefaultTieredAprConfig.tieredAprConfig = transformToAprConfig(
              defaultPriceRulesForOppClient.data,
            );
          }
        }

        for (let i = 0; i < carRules.length; i += 1) {
          const carRuleConfiguration = configurations.find(
            (configuration: { id: string; data: CreditPricing }) =>
              configuration.id ===
              getConfigurationId({
                dealerId: dealer.id,
                condition,
                clientType: getOppositeClientType(clientType),
                modelYearRangeId: carRules[i].id,
              }),
          );

          if (carRuleConfiguration) {
            carRules[i].creditPricing = carRuleConfiguration.data;
            newTieredAprConfigs[i].tieredAprConfig = transformToAprConfig(
              carRuleConfiguration.data,
            );
          }
        }
      }
    }

    return {
      priceRulesWithConfigurations: oppositeClientTypePriceRules,
      newDefaultTieredAprConfig,
      newTieredAprConfigs,
    };
  };

  /**
   * When the user clicks on "Copy Pricing Rules" button, we need to check for two things:
   * Case 1: Check if the current client type priceRules have APR configs set (i.e. Configurations
   *    table have records for the current price Rules) then we have delete those configurations.
   *    Otherwise we will have orphan records which will bring undesired behavior
   * Case 2: Check if the opposite client type prices rules have any configuration set (i.e Configurations
   *    table have records for the opposite client type Price Rules) then we need to get those configurations
   *    and assign those configurations to the corresponding Car Rules
   *
   */
  const copyPriceRulesFromOppositeClientType = async () => {
    const oppositeClientTypePriceRules =
      cloneDeep(
        dealerPriceRules?.data?.[getOppositeClientType(clientType)]?.[
          condition
        ],
      ) ?? getDefaultPriceRulesEntry();

    // Case 1
    const configurationsIdsForDeletionForCurrentClientType =
      getConfigurationIdsForDeletionFromCurrentClientTypePriceRules();

    // Case 2
    const {
      priceRulesWithConfigurations,
      newDefaultTieredAprConfig,
      newTieredAprConfigs,
    } = await getConfigurationsInfoFromOppClientType(
      oppositeClientTypePriceRules,
    );

    if (
      priceRulesWithConfigurations?.defaultRule &&
      !!priceRulesWithConfigurations.defaultRule.aprConfig
    ) {
      priceRulesWithConfigurations.defaultRule.aprConfig = {
        enabled: true,
      };
    }

    priceRulesWithConfigurations.carRules =
      priceRulesWithConfigurations?.carRules?.map((carRule) => {
        if (!!carRule.aprConfig) {
          carRule.aprConfig = {
            enabled: true,
          };
        }
        return carRule;
      }) || [];

    const errorMessagesArray = [];
    const reactRowKeysArray = [];
    const carRuleStatusesArray = [];

    for (
      let index = 0;
      index < priceRulesWithConfigurations.carRules.length;
      index += 1
    ) {
      errorMessagesArray.push({
        maxYear: null,
        minYear: null,
      });
      reactRowKeysArray.push(uuid());
      carRuleStatusesArray.push({
        isNewRecord: false,
      });
    }

    setErrorMessagesForYearRangeEntries(errorMessagesArray);
    setTieredAprConfigs(newTieredAprConfigs);
    setCarRuleStatuses(carRuleStatusesArray);
    setReactRowKeys(reactRowKeysArray);
    setDefaultTieredAprConfig(newDefaultTieredAprConfig);
    setConfigurationIdsForDeletion(
      configurationsIdsForDeletionForCurrentClientType,
    );
    setHasClickedOnCopyButton(true);
    setPriceRules(priceRulesWithConfigurations);

    onChange(false);
  };

  // updates the local price rules state if the dealer changes or a reset is triggered
  useEffect(() => {
    const usedCarPriceRules =
      dealerPriceRules?.data?.[clientType]?.[condition] ||
      getDefaultPriceRulesEntry();

    loadPriceRules(usedCarPriceRules);

    onChange(true);
  }, [dealerPriceRules, clientType, resetTrigger]);

  const [priceRulesRevisions, setPriceRulesRevisions] = useState<
    PriceRulesRevision[] | undefined
  >();
  // gets the price rule revision data when the dealer is loaded and whenever it's updated
  useEffect(() => {
    if (dealer?.id) {
      DealerService.getPriceRulesRevisions(dealer.id)
        .then((r) => setPriceRulesRevisions(orderBy(r, 'updatedAt', 'desc')))
        .catch(() => {
          setErrorMessage(
            'There was an error trying to retrieve pricing history',
          );
        });
    }
  }, [dealer?.id, dealer?.updatedAt]);

  useEffect(() => {
    sendAmplitudeEvent(clientType, condition, Action.START);
  }, [clientType, condition]);

  return (
    <div className="pricing-matrix">
      {errorMessage && <ErrorMessage error={errorMessage} />}
      <div className="row">
        <div className="col-sm-6">
          <Confirm
            onConfirm={() => {
              copyPriceRulesFromOppositeClientType();
            }}
            body={`Are you sure you want to copy rules from ${getClientTypeName(
              getOppositeClientType(clientType),
            )}? This will OVERWRITE all rules on this page.`}
            confirmText="Copy"
            title="Copy Rules"
          >
            <Button>
              <i className="glyphicon glyphicon-duplicate" />
              &nbsp; Copy Pricing Rules from{' '}
              {getClientTypeName(getOppositeClientType(clientType))}
            </Button>
          </Confirm>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <DefaultRulesTableForUsed
            dealer={dealer}
            users={users}
            condition={condition}
            clientType={clientType}
            priceRules={priceRules}
            updatePriceRules={updatePriceRules}
            priceRulesRevisions={priceRulesRevisions}
            dealerPriceRules={dealerPriceRules}
            defaultTieredAprConfig={defaultTieredAprConfig}
            setDefaultTieredAprConfig={setDefaultTieredAprConfig}
          />
        </div>
      </div>
      {priceRules.carRules?.map((carRule, index) => (
        <div className="row" key={reactRowKeys[index]}>
          <div className="col-sm-12">
            <ModelRulesTableForUsed
              index={index}
              dealer={dealer}
              users={users}
              modelPriceRule={carRule}
              clientType={clientType}
              condition={condition}
              priceRules={priceRules}
              updatePriceRules={updatePriceRules}
              priceRulesRevisions={priceRulesRevisions}
              dealerPriceRules={dealerPriceRules}
              updateDeletedAPRConfigs={updateDeletedAPRConfigs}
              currentTieredAPR={tieredAprConfigs[index]}
              errorMessagesForYearRangeEntry={
                errorMessagesForYearRangeEntries[index]
              }
              setTieredAprConfig={setTieredAprConfig}
              deleteTieredAprConfig={deleteTieredAprConfig}
              setErrorMessageForYearInput={setErrorMessageForYearInput}
              deleteModelYearRangeEntry={deleteModelYearRangeEntry}
              isNewModelYearRangeEntry={carRuleStatuses[index].isNewRecord}
            />
          </div>
        </div>
      ))}

      <div className="row shadow-footer">
        <div />
      </div>
      <div className="row save-footer">
        <div className="col-sm-12">
          <Button
            bsStyle="info"
            disabled={fetching}
            onClick={() => {
              addNewModelYearRangeEntry();
            }}
          >
            Add New Range
          </Button>

          <Confirm
            onConfirm={onDealerSaved}
            body="Are you sure you want to save changes? They make take up to 3 hours to reflect on your vehicles."
            confirmText="Save"
            title="Save Changes"
            confirmBSStyle="primary"
          >
            <Button
              bsStyle="primary"
              disabled={fetching || hasInvalidYearInputs()}
            >
              Save Changes
            </Button>
          </Confirm>
          <Confirm
            onConfirm={() => {
              sendAmplitudeEvent(clientType, condition, Action.REVERT);

              setResetTrigger(resetTrigger + 1);
            }}
            body="Are you sure you want to revert changes?"
            confirmText="Revert"
            title="Revert Changes"
          >
            <Button disabled={fetching}>Revert Changes</Button>
          </Confirm>
        </div>
      </div>
    </div>
  );
};
